import React from 'react';

import { ClickEventComponentNames } from 'features/analytics';
import { logButtonClick } from 'features/navigation/utils/analytics';

import { IItem } from '@rbi-ctg/menu';
import ActionButton from 'components/action-button';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { UserSelectionModifierSlotMap } from 'state/product-wizard-v2/types';

import { ProductCtaWrapper } from '../product-cta-wrapper';
import { ActionsWrapper } from '../product-cta.styled';
import { getModifierSelections } from '../utils';

import { getProductComboCtaBtnProps } from './utils';

interface IProductComboCtaCustomization {
  selectedItem: IItem | undefined;
  modifierSelections: UserSelectionModifierSlotMap;
}
export const ProductComboCtaCustomization = ({
  selectedItem,
  modifierSelections,
}: IProductComboCtaCustomization) => {
  const {
    submitCustomizations,
    singleOrMultipleModifierSelection,
    updateSelectionStateWithId,
    productWizardFlowUserSelections,
    setIsBackNavigation,
  } = useProductWizardContext();
  const { slotKeyToCustomize, goToHome, isEdit } = useProductWizardFlow();

  const [submittedCustomization, setSubmittedCustomization] = React.useState<boolean>(false);

  const handleSubmitCustomization = React.useCallback(() => {
    setIsBackNavigation(true);

    setSubmittedCustomization(true);

    if (selectedItem && modifierSelections) {
      const modifierSelectionsEventAttr = Object.values(modifierSelections).map(
        modifierSelection => {
          const key = Object.keys(modifierSelection)[0];
          return modifierSelection[key].modifier.name?.locale ?? '';
        }
      );

      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.PRODUCT_UPDATE_ITEM,
          name: selectedItem.name.locale ?? '',
          Id: selectedItem._id,
          modifierSelections: modifierSelectionsEventAttr,
        },
      });

      // Save customizations
      submitCustomizations({
        modifiers: modifierSelections,
        selectedItem,
        slotKey: slotKeyToCustomize,
      });

      const foundComboSlotUpdates = productWizardFlowUserSelections.find(
        selection => selection.id === slotKeyToCustomize
      );

      // Then reset current combo slot modifications
      if (foundComboSlotUpdates) {
        updateSelectionStateWithId({
          id: slotKeyToCustomize,
          isDirty: false,
          type: foundComboSlotUpdates.type,
        });
      }

      goToHome();
    }
  }, [
    setIsBackNavigation,
    selectedItem,
    modifierSelections,
    submitCustomizations,
    slotKeyToCustomize,
    productWizardFlowUserSelections,
    goToHome,
    updateSelectionStateWithId,
  ]);

  const selections = React.useMemo(
    () =>
      getModifierSelections({
        isEdit,
        singleOrMultipleModifierSelection,
        modifierSelections,
        slotKeyToCustomize,
      }),
    [isEdit, modifierSelections, singleOrMultipleModifierSelection, slotKeyToCustomize]
  );

  const { disabled, label } = React.useMemo(
    () =>
      getProductComboCtaBtnProps({
        selections,
        singleOrMultipleModifierSelection,
        slotKeyToCustomize,
      }),
    [selections, singleOrMultipleModifierSelection, slotKeyToCustomize]
  );

  const Content = (
    <ActionsWrapper>
      <ActionButton
        disabled={submittedCustomization || disabled}
        onPress={handleSubmitCustomization}
        testID="customization-submit-button"
        flex={1}
      >
        {label}
      </ActionButton>
    </ActionsWrapper>
  );

  return <ProductCtaWrapper elements={[{ element: Content }]} />;
};
